import { createRoute, redirect } from '@tanstack/react-router'
import { Suspense, type ComponentType } from 'react'

import { OverviewPage } from '@/features/overview'
import { ClassesPage } from '@/features/classes'
import { CoursesPage } from '@/features/courses'
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage'
import { SessionDetailPage } from '@/features/classes/components/SessionDetailPage'
import { PublicJoinPage } from '@/features/classes/components/PublicJoinPage'
import { PublicBookClassPage } from '@/features/classes/components/PublicBookClassPage'
import { PublicVerifyPage } from '@/features/classes/components/PublicVerifyPage'
import { PublicCertificatePage } from '@/features/classes/components/PublicCertificatePage'
import { ApprovalsPage, AgentSettingsPage } from '@/features/agents'

import { PageLoader } from './layouts'
import { IndexPage } from './IndexPage'
import { rootRoute, authGateRoute, dashboardRoute, adminLayoutRoute } from './route-roots'
import {
  CRMPage,
  ContactDetailPage,
  EmailsPage,
  ReportsPage,
  SettingsPage,
  AdminUsersPage,
  AdminStudentsPage,
  AdminCompaniesPage,
  AdminFinancesPage,
  AdminImportPage,
  AdminCoursesPage,
  AdminSchedulesPage,
  AdminSettingsPage,
  AIPage,
} from './lazy-components'

// ─── Public routes ────────────────────────────────────────────────────────────

const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/join/$token',
  component: function JoinPage() {
    const { token } = joinRoute.useParams()
    const search = joinRoute.useSearch() as { email?: string }
    return <PublicJoinPage token={token} email={search.email} />
  },
})

const embedJoinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/embed/join/$token',
  component: function EmbedJoinPage() {
    const { token } = embedJoinRoute.useParams()
    const search = embedJoinRoute.useSearch() as { email?: string }
    return <PublicJoinPage token={token} email={search.email} embedded />
  },
})

const bookClassRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/book-class',
  component: PublicBookClassPage,
})

const embedBookClassRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/embed/book-class',
  component: function EmbedBookClassPage() {
    return <PublicBookClassPage embedded />
  },
})

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify/$token',
  component: function VerifyPage() {
    const { token } = verifyRoute.useParams()
    return <PublicVerifyPage token={token} />
  },
})

const certificateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/certificate/$token',
  component: function CertificatePage() {
    const { token } = certificateRoute.useParams()
    return <PublicCertificatePage token={token} />
  },
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
})

export const publicRoutes = [
  joinRoute,
  embedJoinRoute,
  bookClassRoute,
  embedBookClassRoute,
  verifyRoute,
  certificateRoute,
  resetPasswordRoute,
]

// ─── Index route ──────────────────────────────────────────────────────────────

export const indexRoute = createRoute({
  getParentRoute: () => authGateRoute,
  path: '/',
  component: IndexPage,
})

// ─── Dashboard routes ─────────────────────────────────────────────────────────

export const overviewRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/overview',
  component: OverviewPage,
})

export const classesRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/classes',
  component: ClassesPage,
})

export const sessionDetailRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/classes/$sessionId',
  component: function SessionDetailRouteComponent() {
    const { sessionId } = sessionDetailRoute.useParams()
    return <SessionDetailPage sessionId={sessionId} />
  },
})

export const coursesRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/courses',
  component: CoursesPage,
})

export const crmRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/crm',
  component: CRMPage,
})

export const crmContactDetailRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/crm/$contactId',
  component: function ContactDetailRouteComponent() {
    const { contactId } = crmContactDetailRoute.useParams()
    return <ContactDetailPage contactId={contactId} />
  },
})

export const emailsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/emails',
  component: EmailsPage,
})

export const reportsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/reports',
  component: ReportsPage,
})

export const reservationsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/reservations',
  beforeLoad: () => {
    throw redirect({ href: '/admin/finances' })
  },
})

export const settingsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/settings',
  component: SettingsPage,
})

export const aiRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/ai',
  component: function AIPageWrapper() {
    return (
      <Suspense fallback={<PageLoader />}>
        <AIPage />
      </Suspense>
    )
  },
})

export const approvalsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/approvals',
  component: ApprovalsPage,
})

export const agentSettingsRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/agents/settings',
  component: AgentSettingsPage,
})

export const dashboardRoutes = [
  overviewRoute,
  classesRoute,
  sessionDetailRoute,
  coursesRoute,
  crmRoute,
  crmContactDetailRoute,
  emailsRoute,
  reportsRoute,
  reservationsRoute,
  settingsRoute,
  aiRoute,
  approvalsRoute,
  agentSettingsRoute,
]

// ─── Admin routes ─────────────────────────────────────────────────────────────

function makeAdminRoute(path: string, Component: ComponentType) {
  return createRoute({
    getParentRoute: () => adminLayoutRoute,
    path,
    component: function AdminPageWrapper() {
      return (
        <Suspense fallback={<PageLoader />}>
          <Component />
        </Suspense>
      )
    },
  })
}

const adminUsersRoute = makeAdminRoute('users', AdminUsersPage)
const adminStudentsRoute = makeAdminRoute('students', AdminStudentsPage)
const adminCompaniesRoute = makeAdminRoute('companies', AdminCompaniesPage)
const adminFinancesRoute = makeAdminRoute('finances', AdminFinancesPage)
const adminImportRoute = makeAdminRoute('import', AdminImportPage)
const adminCoursesRoute = makeAdminRoute('courses', AdminCoursesPage)
const adminSchedulesRoute = makeAdminRoute('schedules', AdminSchedulesPage)
const adminSettingsRoute = makeAdminRoute('settings', AdminSettingsPage)

export const adminRoutes = [
  adminUsersRoute,
  adminStudentsRoute,
  adminCompaniesRoute,
  adminFinancesRoute,
  adminImportRoute,
  adminCoursesRoute,
  adminSchedulesRoute,
  adminSettingsRoute,
]
